import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';

import { useFeatureFlags } from '../../feature-flags';
import { getAuthToken } from '../msal/get-auth-token';

import { graphConfig } from './graph-config';

export async function queryGraph(graphEndpoint: string, accessToken: string, respType = 'json') {
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;

  headers.append('Authorization', bearer);
  headers.append('ConsistencyLevel', 'eventual'); // required for group membershipt lookup

  const options = {
    method: 'GET',
    headers: headers,
  };

  return fetch(graphEndpoint, options)
    .then((response) => (respType === 'json' ? response.json() : response.arrayBuffer()))
    .catch((error) => console.log(error));
}

type GraphWorker = {
  id: string;
  givenName: string;
  jobTitle: string; // wwid
  displayName: string;
  department: string;
  personType: string;
  onPremisesDistinguishedName: string;
};

export const useWorkerQuery = (searchTerm = '') => {
  const { instance } = useMsal();
  const { isFeatureEnabled } = useFeatureFlags();
  // show my frequent contacts (MePeople) if "another user" search term is empty
  const graphEndpoint = searchTerm ? graphConfig.graphUsersEndpoint : graphConfig.graphMePeopleEndpoint;
  let searchQuery: string;
  if (searchTerm !== '') {
    searchQuery = isFeatureEnabled('exclude_generic_accounts')
      ? `?$top=20&$search=("displayName:${searchTerm}" OR "jobTitle:${searchTerm}" OR "mailnickname:${searchTerm}")&$filter=not(startsWith(mailNickname,'grp_'))&$orderBy=displayName&$Select=displayName,givenName,jobTitle,onPremisesDistinguishedName`
      : `?$top=20&$search=("displayName:${searchTerm}" OR "jobTitle:${searchTerm}" OR "mailnickname:${searchTerm}")&$filter=not(startsWith(mailNickname,'grp_'))&$orderBy=displayName`;
  } else {
    searchQuery = '?$top=20&$Select=jobTitle,displayName,department,personType,givenName';
  }

  return useQuery<GraphWorker[]>({
    queryKey: ['worker', searchQuery],
    queryFn: async () => {
      const { accessToken } = await getAuthToken(instance, ['User.Read.All']);
      const workerList = await queryGraph(graphEndpoint + searchQuery, accessToken);
      if (isFeatureEnabled('exclude_generic_accounts')) {
        if (!workerList?.value) {
          return [];
        }
        const containsOnPremisesDistinguishedName = workerList?.value?.some(
          (worker: GraphWorker) => worker.onPremisesDistinguishedName,
        );
        const filteredWorkers = workerList.value.filter(
          (worker: GraphWorker) =>
            worker.onPremisesDistinguishedName && worker.onPremisesDistinguishedName.includes('OU=Workers'),
        );
        return containsOnPremisesDistinguishedName ? filteredWorkers : workerList.value;
      } else {
        return workerList?.value ?? [];
      }
    },
  });
};
